// OverviewComponent
// -----------------
// Dashboard landing page. Moved out of MainDashboard.tsx verbatim — behavior
// is preserved; only the file boundary changed. Stats, recent payments, and
// overdue fees are passed in as props by the parent dashboard.
import React from 'react';
import { Row, Col, Card, Button, List, Tag, Space, Typography } from 'antd';
import {
  UserOutlined, DollarOutlined, ArrowDownOutlined,
  TeamOutlined, BarChartOutlined, BellOutlined, CreditCardOutlined,
  FilePdfOutlined, DashboardOutlined, CheckCircleOutlined, WarningOutlined,
  ExclamationCircleOutlined, BankOutlined, ToolOutlined, SoundOutlined,
} from '@ant-design/icons';

import { useAuth } from '../../../../context/UnifiedAuthContext';
import { useAuthStore } from '../../store/authStore';
import type { Payment, Fee, Owner } from '../../types';
import type { DashboardComponent } from './menuConfig';

import { formatDate } from '../../utils/date';
const { Title, Text } = Typography;

export interface OverviewStats {
  totalOwners: number;
  totalRevenue: number;
  totalExpenses: number;
  netIncome: number;
  pendingFees: number;
  overdueFees: number;
  feeCollectionRate: number;
  occupancyRate: number;
  maintenanceRequests: number;
  openWorkOrders: number;
  activeViolations: number;
}

export interface OverviewProps {
  stats: OverviewStats;
  recentPayments: Payment[];
  overdueFees: Fee[];
  owners: Owner[];
  onNavigate: (section: DashboardComponent) => void;
}

// Progress bar used inside the COLLECTION RATE card.
const ProgressBar: React.FC<{ percentage: number; color: string; bgColor: string; height: number }> =
({ percentage, color, bgColor, height }) => (
  <div style={{
    width: '100%',
    height,
    background: bgColor,
    borderRadius: height / 2,
    overflow: 'hidden',
  }}>
    <div
      style={{
        width: `${percentage}%`,
        height: '100%',
        background: color,
        borderRadius: height / 2,
        transition: 'width 0.3s ease',
      }}
    />
  </div>
);

export const OverviewComponent: React.FC<OverviewProps> = ({
  stats, recentPayments, overdueFees, owners, onNavigate,
}) => {
  const { user: contextUser } = useAuth();
  const { availableCondos, currentCondo } = useAuthStore();
  const isSuperAdmin = contextUser?.role === 'super_admin_condo' || contextUser?.role === 'superadmin';

  // First stat card varies by role: super_admin sees property count, admin
  // sees resident count (with occupancy %).
  const firstCard = isSuperAdmin
    ? { label: 'TOTAL PROPERTIES', value: availableCondos.length || 1, sub: `${stats.totalOwners} RESIDENTS TOTAL` }
    : { label: 'TOTAL RESIDENTS',  value: stats.totalOwners,           sub: `${stats.occupancyRate}% OCCUPANCY` };

  return (
    <div>
      {/* Role badge */}
      {isSuperAdmin && (
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          background: 'linear-gradient(135deg, #eef2ff, #e0e7ff)',
          border: '1px solid #c7d2fe', borderRadius: 8,
          padding: '6px 14px', marginBottom: 20, fontSize: 12, fontWeight: 600, color: '#4f46e5',
        }}>
          <DashboardOutlined /> Super Administrator — viewing: {currentCondo?.name || 'All Properties'}
        </div>
      )}

      {/* Stats Grid with Cards */}
      <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', borderRadius: 12, overflow: 'hidden' }} bodyStyle={{ padding: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: 13, fontWeight: 500 }}>{firstCard.label}</Text>
                <div style={{ fontSize: 32, fontWeight: 700, color: '#fff', margin: '8px 0' }}>{firstCard.value}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Tag color="rgba(255,255,255,0.2)" style={{ color: '#fff', border: 'none', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>{firstCard.sub}</Tag>
                </div>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: 12, padding: 16, backdropFilter: 'blur(10px)' }}>
                {isSuperAdmin ? <BankOutlined style={{ fontSize: 28, color: '#fff' }} /> : <UserOutlined style={{ fontSize: 28, color: '#fff' }} />}
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', borderRadius: 12, overflow: 'hidden' }} bodyStyle={{ padding: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: 13, fontWeight: 500 }}>QUARTER REVENUE</Text>
                <div style={{ fontSize: 32, fontWeight: 700, color: '#fff', margin: '8px 0' }}>${stats.totalRevenue.toLocaleString()}</div>
                {stats.totalRevenue > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <ArrowDownOutlined style={{ color: '#fff', fontSize: 12, transform: 'rotate(45deg)' }} />
                    <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: 12 }}>+12% from last quarter</Text>
                  </div>
                )}
              </div>
              <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: 12, padding: 16, backdropFilter: 'blur(10px)' }}>
                <DollarOutlined style={{ fontSize: 28, color: '#fff' }} />
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', borderRadius: 12, overflow: 'hidden' }} bodyStyle={{ padding: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: 13, fontWeight: 500 }}>COLLECTION RATE</Text>
                <div style={{ fontSize: 32, fontWeight: 700, color: '#fff', margin: '8px 0' }}>{stats.feeCollectionRate}%</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <ProgressBar percentage={stats.feeCollectionRate} color="#fff" bgColor="rgba(255,255,255,0.3)" height={4} />
                </div>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: 12, padding: 16, backdropFilter: 'blur(10px)' }}>
                <BarChartOutlined style={{ fontSize: 28, color: '#fff' }} />
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} style={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', borderRadius: 12, overflow: 'hidden' }} bodyStyle={{ padding: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: 13, fontWeight: 500 }}>NET INCOME</Text>
                <div style={{ fontSize: 32, fontWeight: 700, color: '#fff', margin: '8px 0' }}>${stats.netIncome.toLocaleString()}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Tag color="rgba(255,255,255,0.2)" style={{ color: '#fff', border: 'none', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>
                    PROFIT MARGIN: {stats.totalRevenue > 0 ? ((stats.netIncome / stats.totalRevenue) * 100).toFixed(1) : '0.0'}%
                  </Tag>
                </div>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: 12, padding: 16, backdropFilter: 'blur(10px)' }}>
                <ArrowDownOutlined style={{ fontSize: 28, color: '#fff' }} />
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Second row KPI cards */}
      <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} hoverable onClick={() => onNavigate('maintenance-hub')} style={{ background: 'linear-gradient(135deg, #f6a623 0%, #f5a623 100%)', borderRadius: 12, overflow: 'hidden', cursor: 'pointer' }} bodyStyle={{ padding: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: 13, fontWeight: 500 }}>OPEN WORK ORDERS</Text>
                <div style={{ fontSize: 32, fontWeight: 700, color: '#fff', margin: '8px 0' }}>{stats.openWorkOrders}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Tag color="rgba(255,255,255,0.2)" style={{ color: '#fff', border: 'none', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>
                    {stats.openWorkOrders === 0 ? 'ALL CLEAR' : 'NEEDS ATTENTION'}
                  </Tag>
                </div>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: 12, padding: 16, backdropFilter: 'blur(10px)' }}>
                <ToolOutlined style={{ fontSize: 28, color: '#fff' }} />
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} hoverable onClick={() => onNavigate('violations')}
            style={{
              background: stats.activeViolations > 0
                ? 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)'
                : 'linear-gradient(135deg, #a29bfe 0%, #6c5ce7 100%)',
              borderRadius: 12, overflow: 'hidden', cursor: 'pointer',
            }} bodyStyle={{ padding: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: 13, fontWeight: 500 }}>ACTIVE VIOLATIONS</Text>
                <div style={{ fontSize: 32, fontWeight: 700, color: '#fff', margin: '8px 0' }}>{stats.activeViolations}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Tag color="rgba(255,255,255,0.2)" style={{ color: '#fff', border: 'none', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>
                    {stats.activeViolations === 0 ? 'NO VIOLATIONS' : 'REQUIRES ACTION'}
                  </Tag>
                </div>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: 12, padding: 16, backdropFilter: 'blur(10px)' }}>
                <ExclamationCircleOutlined style={{ fontSize: 28, color: '#fff' }} />
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Two Column Layout */}
      <Row gutter={[24, 24]}>
        <Col xs={24} lg={12}>
          {/* Activity Feed */}
          <Card
            title={
              <Space>
                <div style={{ width: 32, height: 32, background: '#ebf8ff', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <BellOutlined style={{ color: '#3182ce' }} />
                </div>
                <span style={{ fontWeight: 600, fontSize: 16 }}>Recent Activity & Alerts</span>
              </Space>
            }
            bordered={false}
            style={{ borderRadius: 12, marginBottom: 24 }}
            extra={<Button type="link" size="small" style={{ fontWeight: 500 }}>View All</Button>}
          >
            {overdueFees.length > 0 ? (
              <List
                size="large"
                dataSource={overdueFees.slice(0, 4)}
                renderItem={(fee: Fee) => {
                  const owner = owners.find((o: Owner) => o.id === fee.ownerId);
                  return (
                    <List.Item style={{ padding: '16px 0', borderBottom: '1px solid #f0f0f0' }}>
                      <List.Item.Meta
                        avatar={
                          <div style={{ background: '#fff5f5', borderRadius: 8, padding: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <ExclamationCircleOutlined style={{ color: '#f56565', fontSize: 18 }} />
                          </div>
                        }
                        title={
                          <Space>
                            <Text strong style={{ fontSize: 14 }}>Unit {owner?.unitNumber}</Text>
                            <Tag color="error" style={{ margin: 0, borderRadius: 4, fontWeight: 600, fontSize: 11 }}>OVERDUE</Tag>
                          </Space>
                        }
                        description={
                          <div>
                            <Text type="secondary" style={{ fontSize: 13 }}>{fee.description}</Text>
                            <div style={{ marginTop: 4 }}>
                              <Text strong style={{ color: '#f56565', fontSize: 15 }}>${fee.amount}</Text>
                              <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>Due {formatDate(fee.dueDate)}</Text>
                            </div>
                          </div>
                        }
                      />
                    </List.Item>
                  );
                }}
              />
            ) : (
              <div style={{ textAlign: 'center', padding: '32px 0' }}>
                <CheckCircleOutlined style={{ fontSize: 48, color: '#48bb78', marginBottom: 16 }} />
                <Title level={5} style={{ marginBottom: 8, color: '#2d3748' }}>All Accounts Current</Title>
                <Text type="secondary">No overdue payments at this time</Text>
              </div>
            )}
          </Card>

          {/* Performance Metrics */}
          <Card title={<span style={{ fontWeight: 600, fontSize: 16 }}>Performance Metrics</span>} bordered={false} style={{ borderRadius: 12 }}>
            <Row gutter={[16, 16]}>
              {[
                { label: 'Pending Fees',           value: stats.pendingFees,                                                                                 color: '#ed8936' },
                { label: 'Maintenance Requests',   value: stats.maintenanceRequests,                                                                         color: '#4299e1' },
                { label: 'Open Work Orders',       value: stats.openWorkOrders,                                                                              color: '#f6a623' },
                { label: 'Active Violations',      value: stats.activeViolations,                                                                            color: '#f5222d' },
                { label: 'Occupancy Rate',         value: `${stats.occupancyRate}%`,                                                                         color: '#48bb78' },
                { label: 'Expense Ratio',          value: `${stats.totalRevenue > 0 ? ((stats.totalExpenses / stats.totalRevenue) * 100).toFixed(1) : '0.0'}%`, color: '#9f7aea' },
              ].map((metric) => (
                <Col span={12} key={metric.label}>
                  <div style={{ padding: 20, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                    <Text style={{ color: '#718096', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', display: 'block', marginBottom: 8 }}>{metric.label}</Text>
                    <div style={{ fontSize: 28, fontWeight: 700, color: metric.color, lineHeight: 1.2 }}>{metric.value}</div>
                  </div>
                </Col>
              ))}
            </Row>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          {/* Recent Transactions */}
          <Card
            title={
              <Space>
                <div style={{ width: 32, height: 32, background: '#f0fff4', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CreditCardOutlined style={{ color: '#38a169' }} />
                </div>
                <span style={{ fontWeight: 600, fontSize: 16 }}>Recent Transactions</span>
              </Space>
            }
            bordered={false}
            style={{ borderRadius: 12, marginBottom: 24 }}
            extra={<Button type="link" size="small" style={{ fontWeight: 500 }}>View All</Button>}
          >
            {recentPayments.length > 0 ? (
              <List
                size="large"
                dataSource={recentPayments}
                renderItem={(payment: Payment) => {
                  const owner = owners.find((o: Owner) => o.id === payment.ownerId);
                  return (
                    <List.Item style={{ padding: '16px 0', borderBottom: '1px solid #f0f0f0' }}>
                      <List.Item.Meta
                        avatar={
                          <div style={{ background: '#f0fff4', borderRadius: 8, padding: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <CreditCardOutlined style={{ color: '#38a169', fontSize: 18 }} />
                          </div>
                        }
                        title={
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Text strong style={{ fontSize: 14 }}>Unit {owner?.unitNumber}</Text>
                            <Text strong style={{ fontSize: 15, color: '#38a169' }}>${payment.amount}</Text>
                          </div>
                        }
                        description={
                          <div>
                            <Text type="secondary" style={{ fontSize: 13 }}>
                              {formatDate(payment.paymentDate)}
                            </Text>
                            <div style={{ marginTop: 4 }}>
                              <Tag color="green" style={{ margin: 0, borderRadius: 4, fontWeight: 600, fontSize: 11, textTransform: 'uppercase' }}>{payment.method}</Tag>
                              <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>Ref: {payment.id.slice(0, 8)}</Text>
                            </div>
                          </div>
                        }
                      />
                    </List.Item>
                  );
                }}
              />
            ) : (
              <div style={{ textAlign: 'center', padding: '32px 0' }}>
                <CreditCardOutlined style={{ fontSize: 48, color: '#a0aec0', marginBottom: 16 }} />
                <Title level={5} style={{ marginBottom: 8, color: '#2d3748' }}>No Recent Transactions</Title>
                <Text type="secondary">Payment activity will appear here</Text>
              </div>
            )}
          </Card>

          {/* Quick Actions Panel */}
          <Card title={<span style={{ fontWeight: 600, fontSize: 16 }}>Quick Actions</span>} bordered={false} style={{ borderRadius: 12 }}>
            <Row gutter={[12, 12]}>
              {[
                { icon: <FilePdfOutlined />, label: 'Financial Statements', desc: 'Generate & download',      color: '#4299e1', section: 'statements'       as DashboardComponent },
                { icon: <SoundOutlined />,   label: 'Communications',       desc: 'Send notices & emails',    color: '#ed8936', section: 'communications'   as DashboardComponent },
                { icon: <TeamOutlined />,    label: 'Resident Management',  desc: 'Add / manage residents',   color: '#48bb78', section: 'owners'           as DashboardComponent },
                { icon: <DollarOutlined />,  label: 'Payment Processing',   desc: 'Record & process payments',color: '#9f7aea', section: 'payments'         as DashboardComponent },
                { icon: <WarningOutlined />, label: 'Violations',           desc: 'Log & track violations',   color: '#f56565', section: 'violations'       as DashboardComponent },
                { icon: <ToolOutlined />,    label: 'Maintenance',          desc: 'View open requests',       color: '#f6ad55', section: 'maintenance-hub'  as DashboardComponent },
              ].map((action) => (
                <Col span={12} key={action.label}>
                  <Button
                    block
                    icon={React.cloneElement(action.icon, { style: { color: action.color, fontSize: 18 } })}
                    onClick={() => onNavigate(action.section)}
                    style={{
                      height: 64, borderRadius: 10,
                      border: `1.5px solid ${action.color}30`,
                      background: `${action.color}08`,
                      display: 'flex', alignItems: 'center', justifyContent: 'flex-start',
                      padding: '0 14px', cursor: 'pointer', transition: 'all 0.2s',
                    }}
                    onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = `${action.color}18`; (e.currentTarget as HTMLButtonElement).style.borderColor = `${action.color}60`; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = `${action.color}08`; (e.currentTarget as HTMLButtonElement).style.borderColor = `${action.color}30`; }}
                  >
                    <div style={{ marginLeft: 10, textAlign: 'left' }}>
                      <Text style={{ color: action.color, fontWeight: 600, display: 'block', fontSize: 13, lineHeight: 1.3 }}>{action.label}</Text>
                      <Text style={{ color: '#a0aec0', fontSize: 11, display: 'block', lineHeight: 1.2 }}>{action.desc}</Text>
                    </div>
                  </Button>
                </Col>
              ))}
            </Row>
          </Card>
        </Col>
      </Row>
    </div>
  );
};
